import { RecaptchaPlatform } from "@/hooks/use-recaptcha"
import { safeWindow } from "@/lib/safe-window"
import { Result } from "@pacote/result"
import useSWRMutation from "swr/mutation"
import { v4 as uuidV4 } from "uuid"
import { z } from "zod"
import { baseClientFetcher } from "./base-fetchers"
import {
  BaseLoginPayload,
  ComputedLoginFields,
  handleLoginResult,
  LoginErrorResponse,
  loginErrorResponseSchema,
  ssoLoginResponseSchema,
  TloginErrorResponse,
} from "./post-login-sso"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

export type BaseLoginV3Payload = BaseLoginPayload & {
  device?: {
    deviceId?: string
    deviceName?: string
    deviceDescription?: string
    pushId?: string
  }
}

type SsoLoginV3Payload = BaseLoginV3Payload & {
  pvd?: string
  platform: RecaptchaPlatform
}

export const ssoLoginV3ResponseSchema = z.object({
  ...ssoLoginResponseSchema.shape,
  challengeRequired: z.boolean().optional(),
  loginPendingId: z.string().optional(),
  trustedDevices: z
    .array(
      z.object({
        pushId: z.string().optional(),
        deviceName: z.string().optional(),
        lastLoginDate: z.number().optional(),
        channel: z.number().optional(),
      }),
    )
    .optional(),
  mfaMethods: z.array(z.enum(["push", "sms", "email"])).optional(),
  mfaEnabled: z.boolean().optional(),
})

export type SsoLoginV3Response = z.infer<typeof ssoLoginV3ResponseSchema>

export type TSsoLoginV3Response = SsoLoginV3Response & ComputedLoginFields

type SsoLoginV3Result = Result<TSsoLoginV3Response, TloginErrorResponse>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

export const ssoLoginV3Endpoint = "/authentication-ms/v3/sso/login"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const ssoLoginV3Fetcher = async (
  key: string,
  { arg }: { arg: SsoLoginV3Payload },
): Promise<SsoLoginV3Result> => {
  const sessionId = uuidV4()

  const loginServiceResult = await baseClientFetcher<
    SsoLoginV3Response,
    LoginErrorResponse
  >(
    {
      url: key,
      method: "POST",
      headers: {
        "X-Session-Id": sessionId,
        ...(safeWindow()?.APP_VENDOR === "sisal"
          ? { "x-set-queue": "true" }
          : {}),
      },
      body: {
        username: arg.username,
        password: arg.password,
        recaptchaToken: arg.recaptchaToken,
        platform: arg.platform,
        device: {
          deviceId: arg.device?.deviceId,
        },
      },
      successSchemaValidator: ssoLoginV3ResponseSchema,
      errorSchemaValidator: loginErrorResponseSchema,
      throwOnValidationError: true,
    },
    { enrichRequestData: false },
  )

  return await handleLoginResult(loginServiceResult, arg.channel, sessionId)
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

export const useSsoLoginV3Mutation = () =>
  useSWRMutation(ssoLoginV3Endpoint, ssoLoginV3Fetcher, {
    populateCache: true,
  })
